import React, { useState, useMemo } from 'react';

/**
 * Reusable sharing picker for creation modals.
 * Matches the ShareModal UI: toggle, search, select/deselect all, avatar list with "(you)".
 */
const SharingPicker = ({ users, selectedIds, onSelectedIdsChange, shareWithAll, onShareWithAllChange, currentUserId }) => {
  const [searchQuery, setSearchQuery] = useState('');

  // Exclude current user from the list — they are always included automatically
  const otherUsers = useMemo(() => users.filter(u => u.id !== currentUserId), [users, currentUserId]);

  const filteredUsers = useMemo(() => {
    let list = otherUsers;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(u =>
        (u.name || '').toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q)
      );
    }
    // Sort: selected users first, then unselected
    return [...list].sort((a, b) => {
      const aSel = selectedIds.has(a.id) ? 1 : 0;
      const bSel = selectedIds.has(b.id) ? 1 : 0;
      return bSel - aSel;
    });
  }, [otherUsers, searchQuery, selectedIds]);

  const handleToggleUser = (userId) => {
    const next = new Set(selectedIds);
    if (next.has(userId)) {
      next.delete(userId);
    } else {
      next.add(userId);
    }
    onSelectedIdsChange(next);
  };

  const handleSelectAll = () => {
    const all = new Set(otherUsers.map(u => u.id));
    if (currentUserId) all.add(currentUserId);
    onSelectedIdsChange(all);
  };

  const handleDeselectAll = () => {
    onSelectedIdsChange(new Set(currentUserId ? [currentUserId] : []));
  };

  return (
    <div className="mb-4">
      <label className="block text-xs font-medium uppercase tracking-wider mb-2" style={{ color: 'rgba(255,255,255,0.5)' }}>
        Sharing
      </label>

      {/* Share with everyone toggle */}
      <label className="flex items-center gap-3 mb-2 cursor-pointer select-none">
        <div
          className="relative w-9 h-5 rounded-full transition-colors"
          style={{ backgroundColor: shareWithAll ? 'var(--tqa-green)' : 'var(--tqa-surface-3)' }}
          onClick={() => onShareWithAllChange(!shareWithAll)}
        >
          <div
            className="absolute top-0.5 w-4 h-4 rounded-full transition-transform"
            style={{ backgroundColor: 'white', transform: shareWithAll ? 'translateX(18px)' : 'translateX(2px)' }}
          />
        </div>
        <span className="text-xs uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.7)' }}>
          Share with everyone
        </span>
      </label>

      {/* User selection */}
      {!shareWithAll && (
        <div>
          {/* Search */}
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-3 py-2 text-xs text-white focus:outline-none mb-2"
            style={{ backgroundColor: 'var(--tqa-surface-2)', border: '1px solid var(--tqa-border-dark)' }}
            placeholder="Search users..."
          />

          {/* Select all / deselect all */}
          <div className="flex gap-3 mb-2">
            <button
              type="button"
              onClick={handleSelectAll}
              className="text-xs uppercase tracking-wider transition-colors hover:brightness-125"
              style={{ color: 'var(--tqa-green)' }}
            >
              Select all
            </button>
            <button
              type="button"
              onClick={handleDeselectAll}
              className="text-xs uppercase tracking-wider transition-colors hover:brightness-125"
              style={{ color: 'rgba(255,255,255,0.5)' }}
            >
              Deselect all
            </button>
          </div>

          {/* User list */}
          <div className="overflow-y-auto max-h-40" style={{ backgroundColor: 'var(--tqa-surface-2)', border: '1px solid var(--tqa-border-dark)', scrollbarWidth: 'thin' }}>
            {filteredUsers.map(user => (
                <label
                  key={user.id}
                  className="flex items-center gap-3 px-2 py-1.5 cursor-pointer transition-colors hover:brightness-110"
                  style={{ backgroundColor: selectedIds.has(user.id) ? 'rgba(130,255,207,0.06)' : 'transparent' }}
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.has(user.id)}
                    onChange={() => handleToggleUser(user.id)}
                    className="accent-[var(--tqa-green)] w-3.5 h-3.5 flex-shrink-0"
                  />
                  {user.avatarUrl ? (
                    <img src={user.avatarUrl} alt="" className="w-5 h-5 rounded-full flex-shrink-0" />
                  ) : (
                    <div className="w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center text-[10px] font-bold"
                      style={{ backgroundColor: 'var(--tqa-surface-3)', color: 'rgba(255,255,255,0.5)' }}>
                      {(user.name || user.email || '?')[0].toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-xs text-white truncate">{user.name || 'Unnamed'}</div>
                    <div className="text-[10px] truncate" style={{ color: 'rgba(255,255,255,0.4)' }}>{user.email}</div>
                  </div>
                </label>
            ))}
            {filteredUsers.length === 0 && (
              <div className="py-3 text-center text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
                No users found
              </div>
            )}
          </div>

          {/* Selection count */}
          <div className="mt-1 text-[10px]" style={{ color: 'rgba(255,255,255,0.4)' }}>
            {selectedIds.size - (currentUserId && selectedIds.has(currentUserId) ? 1 : 0)} of {otherUsers.length} users selected
          </div>
        </div>
      )}
    </div>
  );
};

export default SharingPicker;
